import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Button, FormControl, Table} from "react-bootstrap";
import {
    assignSim,
    getSimHistory,
    patchSim,
    unassignSim,
} from "../utils/bedrockAPI";
import {apiErrorMessage, formatDate, sticker} from "../utils/unitDisplay";
import {
    SIM_CARRIERS,
    SIM_STATUSES,
    findUnitBySticker,
} from "../utils/simDisplay";

export default function SimExpandPanel({sim, units, onSimPatched}) {
    const [iccid, setIccid] = useState(sim.iccid || "");
    const [phone, setPhone] = useState(sim.phoneNumber || "");
    const [carrier, setCarrier] = useState(sim.carrier || "ATT");
    const [status, setStatus] = useState(sim.status || "INVENTORY");
    const [notes, setNotes] = useState(sim.notes || "");
    const [imei, setImei] = useState(sim.imei || "");
    const [stickerInput, setStickerInput] = useState("");
    const [history, setHistory] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        setIccid(sim.iccid || "");
        setPhone(sim.phoneNumber || "");
        setCarrier(sim.carrier || "ATT");
        setStatus(sim.status || "INVENTORY");
        setNotes(sim.notes || "");
        setImei(sim.imei || "");
    }, [sim.simId]);

    useEffect(() => {
        setStatus(sim.status || "INVENTORY");
    }, [sim.status, sim.unitId]);

    useEffect(() => {
        let cancelled = false;
        setHistory(null);
        (async () => {
            try {
                const rows = await getSimHistory(sim.simId);
                if (!cancelled) setHistory(Array.isArray(rows) ? rows : []);
            } catch (err) {
                if (!cancelled) setError(apiErrorMessage(err));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sim.simId]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        try {
            const updated = await patchSim(sim.simId, {
                iccid: iccid.trim() || null,
                phoneNumber: phone.trim() || null,
                carrier,
                status,
                notes: notes.trim() || null,
                imei: imei.trim() || null,
            });
            onSimPatched(updated);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSaving(false);
    };

    const handleAssign = async () => {
        const {unit, matches} = findUnitBySticker(units, stickerInput);
        if (!unit) {
            setError(
                matches.length > 1
                    ? "That sticker matches more than one unit."
                    : "No unit found for that sticker (use the first 8 characters of the unit ID)."
            );
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const updated = await assignSim(sim.simId, unit.unitId);
            onSimPatched(updated);
            setStickerInput("");
            const rows = await getSimHistory(sim.simId).catch(() => history);
            setHistory(Array.isArray(rows) ? rows : history);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSaving(false);
    };

    const handleUnassign = async () => {
        if (!sim.unitId) return;
        if (!window.confirm("Unassign this SIM? It goes back to the drawer.")) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const updated = await unassignSim(sim.simId);
            onSimPatched(updated);
            const rows = await getSimHistory(sim.simId).catch(() => history);
            setHistory(Array.isArray(rows) ? rows : history);
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSaving(false);
    };

    const assignedUnit = sim.unitId
        ? (units || []).find((unit) => unit.unitId === sim.unitId)
        : null;

    return (
        <div className="units-expand">
            {error && <div className="units-error">{error}</div>}
            <form onSubmit={handleSave}>
            <div className="units-expand-grid">
                <div>
                    <span className="units-meta-label">ICCID</span>
                    <FormControl value={iccid} onChange={(e) => setIccid(e.target.value)} />
                </div>
                <div>
                    <span className="units-meta-label">Phone</span>
                    <FormControl value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div>
                    <span className="units-meta-label">Carrier</span>
                    <FormControl
                        componentClass="select"
                        value={carrier}
                        onChange={(e) => setCarrier(e.target.value)}
                    >
                        {SIM_CARRIERS.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </FormControl>
                </div>
                <div>
                    <span className="units-meta-label">Status</span>
                    <FormControl
                        componentClass="select"
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {SIM_STATUSES.map((name) => (
                            <option key={name} value={name}>
                                {name}
                            </option>
                        ))}
                    </FormControl>
                </div>
                <div>
                    <span className="units-meta-label">IMEI</span>
                    <FormControl value={imei} onChange={(e) => setImei(e.target.value)} />
                </div>
            </div>
            <div className="units-notes-wide">
                <span className="units-meta-label">Notes</span>
                <FormControl
                    componentClass="textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="SIM notes"
                />
            </div>
            <div className="units-detail-actions">
                <Button type="submit" bsStyle="primary" bsSize="small" disabled={saving}>
                    {saving ? "Saving…" : "Save SIM"}
                </Button>
            </div>
            </form>

            <h3 className="units-expand-heading">Assigned unit</h3>
            <div className="units-expand-grid">
                <div>
                    <span className="units-meta-label">Current unit</span>
                    <div className="units-meta-value">
                        {sim.unitId ? (
                            <Link to={`/admin/units/${sim.unitId}`}>
                                {sticker(sim.unitId)}
                                {assignedUnit?.architecture ? ` · ${assignedUnit.architecture}` : ""}
                            </Link>
                        ) : (
                            "Drawer (unassigned)"
                        )}
                    </div>
                </div>
            </div>
            <div className="units-note-form">
                <FormControl
                    type="text"
                    placeholder="Unit sticker (first 8 of unit ID)"
                    value={stickerInput}
                    onChange={(e) => setStickerInput(e.target.value)}
                    style={{flex: "0 1 220px"}}
                />
                <Button
                    bsStyle="primary"
                    bsSize="small"
                    disabled={saving || stickerInput.trim().length < 8}
                    onClick={handleAssign}
                >
                    {sim.unitId ? "Move to unit" : "Assign to unit"}
                </Button>
                {sim.unitId && (
                    <Button bsStyle="warning" bsSize="small" disabled={saving} onClick={handleUnassign}>
                        Unassign
                    </Button>
                )}
            </div>

            <h3 className="units-expand-heading">Assignment history</h3>
            {history == null ? (
                <div className="units-empty">Loading history…</div>
            ) : history.length === 0 ? (
                <div className="units-empty">Never assigned.</div>
            ) : (
                <Table striped condensed className="units-table">
                    <thead>
                        <tr>
                            <th>Unit</th>
                            <th>Assigned</th>
                            <th>Removed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map((row) => (
                            <tr key={row.simAssignmentId}>
                                <td>
                                    <Link to={`/admin/units/${row.unitId}`}>{sticker(row.unitId)}</Link>
                                </td>
                                <td>{formatDate(row.assignedAt)}</td>
                                <td>{row.unassignedAt ? formatDate(row.unassignedAt) : "current"}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
